import {testIdProps} from '@github-ui/test-id-props'
import {ArrowRightIcon, PlusCircleIcon, RepoIcon} from '@primer/octicons-react'
import {Spinner, useMergedRefs} from '@primer/react'
import {useRef} from 'react'

import type {Repository} from '../api/common-contracts'
import type {RepositoryItem} from '../api/repository/contracts'
import type {GetItemPropsAdditionalHandlers} from '../hooks/common/use-autocomplete'
import {usePrefixedId} from '../hooks/common/use-prefixed-id'
import {useMemexItems} from '../state-providers/memex-items/use-memex-items'
import {PickerItem, PickerList, useAdjustPickerPosition} from './common/picker-list'
import {Portal} from './common/portal'
import styles from './suggested-item-list.module.css'
import {SuggestedItemOption} from './suggested-item-option'

type AuxillaryMetadata = {
  title?: string
  repository: Repository
}

export type ADD_MULTIPLE_ITEMS_KEY = 'add_multiple_items'
export const ADD_MULTIPLE_ITEMS_KEY: ADD_MULTIPLE_ITEMS_KEY = 'add_multiple_items'
export type ADD_MULTIPLE_ITEMS = {type: ADD_MULTIPLE_ITEMS_KEY} & AuxillaryMetadata

export type CREATE_ISSUE_KEY = 'create_issue'
export const CREATE_ISSUE_KEY: CREATE_ISSUE_KEY = 'create_issue'
export type CREATE_ISSUE = {type: CREATE_ISSUE_KEY} & AuxillaryMetadata

interface SuggestedItemListProps extends React.HTMLAttributes<HTMLUListElement> {
  isOpen: boolean
  loading: boolean
  items: Array<RepositoryItem | CREATE_ISSUE | ADD_MULTIPLE_ITEMS>
  containerRef: React.RefObject<HTMLDivElement | null>
  getItemProps: (
    item: RepositoryItem | CREATE_ISSUE | ADD_MULTIPLE_ITEMS,
    index: number,
    additionalHandlers?: GetItemPropsAdditionalHandlers,
  ) => React.LiHTMLAttributes<HTMLLIElement>
  itemOnMouseDown: (
    event: React.MouseEvent<HTMLElement>,
    item: RepositoryItem | CREATE_ISSUE | ADD_MULTIPLE_ITEMS,
  ) => void
}

export const SuggestedItemList = ({
  ref: forwardedRef,
  loading,
  items,
  getItemProps,
  isOpen,
  itemOnMouseDown,
  containerRef,
  ...listProps
}: SuggestedItemListProps & {ref: React.Ref<HTMLUListElement>}) => {
  const ref = useRef<HTMLUListElement>(null)
  const mergedRef = useMergedRefs(forwardedRef, ref)
  const {items: memexItems} = useMemexItems()
  // Ensure the picker list position is updated when new items are added to the view
  const {adjustPickerPosition} = useAdjustPickerPosition(containerRef, ref, isOpen, [loading, items, memexItems], {
    yAlign: 'top',
  })
  const portalId = usePrefixedId('__omnibarPortalRoot__')
  return (
    <Portal id={portalId} onMount={adjustPickerPosition}>
      <PickerList
        {...listProps}
        {...testIdProps('issue-picker-list')}
        ref={mergedRef}
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        className={`issue-picker-list omnibar-picker ${!isOpen || (!loading && !items.length) ? 'hidden' : 'visible'}`}
        hidden={!isOpen || (!loading && !items.length)}
        aria-label="Suggestions"
      >
        {loading ? (
          <div className={styles.Box}>
            <Spinner size="medium" />
          </div>
        ) : (
          items.map((item, index) => {
            if (item.type === CREATE_ISSUE_KEY) {
              return (
                <PickerItem
                  key="create"
                  style={{borderTop: '1px solid', borderColor: 'var(--borderColor-muted)'}}
                  {...getItemProps(item, index, {onMouseDown: event => itemOnMouseDown(event, item)})}
                  {...testIdProps('create-new-issue')}
                >
                  <div className={styles.Box_1}>
                    <div className={styles.Box_2}>
                      <PlusCircleIcon className={styles.Octicon} />
                    </div>
                    <span className={styles.Text}>
                      Create new issue{' '}
                      {item.title ? (
                        <>
                          &quot;<span className={styles.Text_1}>{item.title}</span>&quot;
                        </>
                      ) : null}
                    </span>
                  </div>
                </PickerItem>
              )
            } else if (item.type === ADD_MULTIPLE_ITEMS_KEY) {
              return (
                <PickerItem
                  key="multiple"
                  {...getItemProps(item, index, {onMouseDown: event => itemOnMouseDown(event, item)})}
                  {...testIdProps('add-multiple-items')}
                >
                  <div className={styles.Box_1}>
                    <div className={styles.Box_2}>
                      <RepoIcon className={styles.Octicon} />
                    </div>
                    <span className={styles.Text}>
                      Add items from <span className={styles.Text_1}>{item.repository.nameWithOwner}</span>
                    </span>
                    <div className={styles.Box_3}>
                      <ArrowRightIcon className={styles.Octicon} />
                    </div>
                  </div>
                </PickerItem>
              )
            } else {
              return (
                <SuggestedItemOption
                  key={item.number}
                  item={item}
                  {...getItemProps(item, index, {onMouseDown: event => itemOnMouseDown(event, item)})}
                  {...testIdProps('issue-picker-item')}
                />
              )
            }
          })
        )}
      </PickerList>
    </Portal>
  )
}

SuggestedItemList.displayName = 'SuggestedItemList'
